import React, { useState, MouseEvent, useRef } from "react";
import {
  Button,
  Grid,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DoubleCell from "./DoubleCell";
import useHoverStyles from "./transactionHooks/useHoverStyles";
import { Phone } from "@mui/icons-material";
import CnbLogoMobile from "src/icons/CnbLogoMobile";
import {
  useGetUserDatav2Query,
  useLazyCreateAccountReportQuery,
} from "src/store/features/api/generated";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { timestampFormatter } from "src/utils/helpers/DateAndTimeHelpers";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import autoTable, { CellHookData, RowInput, UserOptions } from "jspdf-autotable";
import { formatTableHeader, formatTableTitle } from "../reports/utils/ReportTools";
import { trackEvent } from "src/Amplitude";
import { getUserTimeZone } from "src/utils/hooks/useTimeZone";
import {
  disclaimerMessageEOFY,
  formatBusinessFriendlyName,
  formatTimestampToDate,
  getFinancialYearDates,
  sortAllArraysByDate,
  transformDatesInObject,
} from "src/utils/financialYearReportUtils";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import EofyButtonIcon from "src/icons/transactions/EofyButtonIcon";

const EofyStatementButton: React.FC = () => {
  // ---------------------------------------------STATE--------------------------------------------------
  const isMobile = useIsMobile();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const headerRef = useRef<any>();
  const footerRef = useRef<any>();

  const [compLoading, setCompLoading] = useState<boolean>(false);

  // rather than hardcoding years for options, generate dynamically from the
  // current year moving backwards until 2022, which is the latest year we have data for
  const yearOptions: number[] = [];
  const now: Date = new Date();
  const currentYear: number = now.getFullYear();
  const currentMonth: number = now.getMonth() + 1; // month is 0-indexed
  for (let year = currentYear; year >= 2022; year--) {
    if (year === currentYear) {
      // if it's the current year, only include the option if it's after the end of the financial year
      if (currentMonth < 7) continue; // july is the 7th month and the start of the financial year
    }

    yearOptions.push(year);
  }

  // ---------------------------------------------RTKQ--------------------------------------------------
  const { data: userData, isSuccess } = useGetUserDatav2Query();
  const [fetchAccountReport, { data: reportData, isSuccess: reportIsSuccess, error }] =
    useLazyCreateAccountReportQuery();

  // ---------------------------------------------STYLES & THEME--------------------------------------------------
  const theme = useTheme();
  const showText = useMediaQuery(theme.breakpoints.between(900, 1070));
  const {
    onEnter: handleSummaryHoverEnter,
    onLeave: handleSummaryHoverLeave,
    styles: summaryHoverStyles,
  } = useHoverStyles();

  // ---------------------------------------------HANDLERS--------------------------------------------------
  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleYearSelect = (year: number) => {
    handleClose();

    const { startDate, endDate } = getFinancialYearDates(year);
    generateFinancialYearReport(startDate, endDate, year);
  };

  // ---------------------------------------------REPORT GENERATION--------------------------------------------------
  const generateFinancialYearReport = (startDate: number, endDate: number, curryear: number) => {
    setCompLoading(true);
    fetchAccountReport({ timezone: getUserTimeZone, startDate, endDate })
      .unwrap()
      .then(async (data: any) => {
        // Sort all arrays by date
        data = await sortAllArraysByDate(data);

        // Convert US date format to AU date format
        data = await transformDatesInObject(data);

        let pdf = new jsPDF();

        const formattedStartDate = formatTimestampToDate(startDate);
        const formattedEndDate = formatTimestampToDate(endDate);

        // Creating header and footer from JSX
        if (isSuccess) {
          headerRef.current.style.display = "flex";
          footerRef.current.style.display = "flex";

          const canvas = await Promise.all([
            html2canvas(headerRef.current),
            html2canvas(footerRef.current),
          ]);

          const imageUrlHeader = canvas[0].toDataURL("image/png");
          const imageUrlFooter = canvas[1].toDataURL("image/png");

          const elements: any[] = [];
          const titles: string[] = [];
          const rowColor = ["#E5E7E8", "#FCF6EB"];
          const imgPropertiesHeader = pdf.getImageProperties(imageUrlHeader);
          const imgPropertiesFooter = pdf.getImageProperties(imageUrlFooter);

          const pdfWidth = pdf.internal.pageSize.getWidth();
          const pdfHeightHeader =
            (imgPropertiesHeader.height * pdfWidth) / imgPropertiesHeader.width;
          const pdfHeightFooter =
            (imgPropertiesFooter.height * pdfWidth) / imgPropertiesFooter.width;
          let currentY = 105; // Adjusting starting Y position for start of document

          // Draw header image first
          pdf.addImage(imageUrlHeader, "PNG", 0, 0, pdfWidth, pdfHeightHeader);

          const ignoreList: string[] = ["accountName", "totalPortfolioValue", "defaultCurrency"];

          // Splitting Title and content
          for (const [title, titleData] of Object.entries(data)) {
            if (ignoreList.some((toIgnore: string) => title.includes(toIgnore))) {
              continue;
            }
            titles.push(title);

            if (Array.isArray(titleData) && titleData.length > 0) {
              // holdings require an additional disclaimer
              if (title.includes("holding")) {
                // add disclaimer in a smaller font size
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(8);
                pdf.setTextColor("#1D3557");
                pdf.text(
                  `Holdings as at ${formattedEndDate} \n *All listed rates are approximate as at ${formattedEndDate}`,
                  15,
                  pdfHeightHeader + currentY - 55, // Adjusted Y position
                  {
                    align: "left",
                  },
                );

                // reset font size and color back to default
                pdf.setFont("helvetica", "normal");
                pdf.setFontSize(12);
              }

              elements.push(titleData);
            } else {
              // Add empty table if returned data is empty
              const businessFriendlyTitle = formatBusinessFriendlyName(title);

              let noDataMessage: string;
              if (title.includes("holding")) {
                noDataMessage = `No ${businessFriendlyTitle} at ${formattedEndDate}`;
              } else {
                noDataMessage = `No ${businessFriendlyTitle} between ${formattedStartDate} to ${formattedEndDate}`;
              }

              elements.push([
                {
                  [noDataMessage]: "",
                },
              ]);
            }
          }

          // Generating PDF from raw data
          elements.forEach((element, iterator) => {
            if (Array.isArray(element) && element.length > 0) {
              let headers: string[];
              // the Trades table will also need to add the headers from the lines of the trades
              if (Object.hasOwn(element[0], "lines")) {
                const { lines, ...rest } = element[0] as any;
                const lineHeaders: string[] = Object.keys(lines[0]);
                const restHeaders: string[] = Object.keys(rest);

                headers = restHeaders.concat(lineHeaders);
              } else {
                headers = Object.keys(element[0]);
              }
              const tableHeader = [headers].map((el) => formatTableHeader(el));

              // holdings prices are approximate, so need to adjust column header to reflect this
              if (titles[iterator].includes("holding") && tableHeader[0].length > 1) {
                const priceHeaderIndex = tableHeader[0].findIndex((el) => el === "Price");
                tableHeader[0][priceHeaderIndex] = "Price*";
              }

              const tableBody: RowInput[] = [];
              element.forEach((x) => {
                // the Trades table will need to loop through each line of the trade to get all the values
                if (Object.hasOwn(x, "lines")) {
                  const { lines, ...rest } = x;
                  const restValues: string[] = Object.values(rest);

                  lines.forEach((line: any) => {
                    const lineValues: string[] = Object.values(line);
                    const values = restValues.concat(lineValues);
                    tableBody.push(values);
                  });

                  // Add an empty line after processing an order
                  tableBody.push([]);
                } else {
                  tableBody.push(Object.values(x));
                }
              });

              const table: UserOptions = {
                head: tableHeader,
                body: tableBody,
                theme: "grid",
                startY: currentY,
                rowPageBreak: "avoid",
                bodyStyles: { valign: "top", fontSize: 7 },
                headStyles: {
                  fillColor: theme.palette.primary.main,
                  fontSize: 7,
                },
                margin: {
                  top: pdfHeightFooter + 30,
                  bottom: pdfHeightFooter + 20,
                },
                didDrawPage: function (data) {
                  // Header on all pages except the first page
                  pdf.addImage(imageUrlHeader, "PNG", 0, 0, pdfWidth, pdfHeightHeader);

                  if (data.pageNumber === 1) {
                    // Add custom text below the header image
                    pdf.setFontSize(16);
                    pdf.setFont("helvetica", "bold");
                    pdf.setTextColor("#1D3557");
                    pdf.text(
                      `End of Financial Year Statement ` + curryear,
                      pdfWidth / 2,
                      pdfHeightHeader + 10, // Adjusted Y position
                      {
                        align: "center",
                      },
                    );
                    pdf.setFont("helvetica", "normal");
                    pdf.setFontSize(12);
                    pdf.text(
                      `${timestampFormatter(startDate, true)} - ${timestampFormatter(
                        endDate,
                        true,
                      )}`,
                      pdfWidth / 2,
                      pdfHeightHeader + 15, // Adjusted Y position
                      {
                        align: "center",
                      },
                    );
                    pdf.setFontSize(10);

                    // Wrap the customer id + name to the page width so a long name
                    // wraps onto extra lines instead of overflowing off the right
                    // edge, and drop the address below however many lines it takes.
                    const textLeftMargin = 15;
                    const textMaxWidth = pdfWidth - textLeftMargin * 2;
                    const textLineHeight = 5;

                    pdf.setFont("helvetica", "bold");
                    const nameLines: string[] = pdf.splitTextToSize(
                      userData?.details.customerId + "  " + userData?.details.name,
                      textMaxWidth,
                    );
                    pdf.text(nameLines, textLeftMargin, pdfHeightHeader + 25, {
                      align: "left",
                    });

                    pdf.setFont("helvetica", "normal");
                    const addressY = pdfHeightHeader + 25 + nameLines.length * textLineHeight;
                    const addressLines: string[] = pdf.splitTextToSize(
                      userData?.address.fullAddress,
                      textMaxWidth,
                    );
                    pdf.text(addressLines, textLeftMargin, addressY, {
                      align: "left",
                    });
                  }

                  // Footer on all pages
                  pdf.setFontSize(10);
                  pdf.addImage(imageUrlFooter, "PNG", 0, 282, pdfWidth, pdfHeightFooter);
                },
                didParseCell: function (HookData: CellHookData) {
                  if (HookData.section !== "head") {
                    HookData.cell.styles.fillColor = rowColor[1];
                  }
                  // deposits can have really long txIds, which will compress the other columns and make the cell content look weird.
                  // so we set a minimum width for the deposits table so that the non-txId columns are not compressed
                  if (titles[iterator].toLowerCase().includes("deposits")) {
                    HookData.cell.styles.minCellWidth = 20;
                  }
                  if (HookData.cell.text.toString() === "") {
                    HookData.cell.styles.fillColor = "#F8F9F9";
                    HookData.cell.styles.cellPadding = 0.1;
                  }
                },
              };

              pdf.setTextColor(theme.palette.primary.main);

              const formattedTitles = titles.map((el) => formatTableTitle(el));
              pdf.text(formattedTitles[iterator], 15, currentY - 5);

              autoTable(pdf, table); // Cast to 'any' to avoid TypeScript errors
              pdf.addPage();
            }
          });

          pdf.setFontSize(12);
          pdf.text(disclaimerMessageEOFY, 20, 20);

          pdf.save(`${userData?.details.customerId ?? ""}_Caleb&Brown_EOFY_Statement_${curryear}`);

          setCompLoading(false);
          trackEvent(
            "Download EOFY Summary Report",
            { timezone: getUserTimeZone, startDate, endDate },
            "Export EOFY Report",
          );
        } else {
          console.log("Error: Download Account Summary PDF " + error);
        }
      })
      .catch((err) => {
        console.error("ERROR: Download Transactions Summary CSV " + err);
        setCompLoading(false);
      });
  };

  // ---------------------------------------------PERMISSION CHECK--------------------------------------------------
  const isAU: boolean = userData?.address.country === "AU"; // this feature will only be availble to AU users
  if (!isAU) {
    return null;
  }

  // ---------------------------------------------DESIGN--------------------------------------------------
  const Header = () => {
    return (
      <>
        <Grid
          container
          ref={headerRef}
          minWidth={"1044px"}
          position="relative"
          sx={{ display: "none" }}>
          <Grid
            item
            xs={12}
            sx={{
              background: theme.palette.primary.main,
              border: "none",
            }}>
            <div
              style={{
                margin: "12px 12px 10px 12px",
                border: "none",
              }}></div>
          </Grid>
          <Grid
            item
            xs={12}
            py={4}
            sx={{
              background: theme.palette.primary.main,
              alignItems: "center",
            }}>
            <CalebAndBrownLogo height={56} />
          </Grid>

          <Grid
            item
            xs={12}
            textAlign="center"
            pb={3}
            sx={{
              background: theme.palette.primary.main,
              alignItems: "center",
              color: "#FFF",
            }}>
            <Typography> CALEB AND BROWN PTY LTD</Typography>
            <Typography> ABN: 66619023559</Typography>
          </Grid>
        </Grid>
      </>
    );
  };

  const Footer = () => {
    return (
      <>
        <Grid
          container
          minWidth={"1044px"}
          ref={footerRef}
          sx={{
            background: theme.palette.primary.main,
            position: "relative",
            display: "none",
          }}>
          <Grid item xs={1} p={2} sx={{ marginLeft: "55px", border: "none" }}>
            <CnbLogoMobile color={"#F8F9F9"} width={35} />
          </Grid>
          <div
            style={{
              position: "absolute",
              borderLeft: "2px solid #F8F9F9",
              height: "80%",
              top: "5px",
              left: "130px",
            }}
          />
          <Grid item sx={{ position: "relative", top: "15px", left: "10px" }}>
            <Stack>
              <Typography color={"#F8F9F9"}>{userData?.broker.name}</Typography>
              <Typography color={"#F8F9F9"}>{userData?.broker.email}</Typography>
            </Stack>
          </Grid>
          <Grid
            item
            p={2}
            sx={{
              position: "absolute",
              height: "80%",
              right: "10px",
            }}>
            <Stack direction="row" sx={{ marginRight: "50px" }}>
              <Phone
                sx={{
                  fontSize: "16px",
                  marginRight: "4px",
                  color: "#F8F9F9",
                  marginTop: "3px",
                }}
              />
              <Typography sx={{ color: "#F8F9F9", marginTop: "1px" }}>
                {userData?.broker.phoneNumberAU}
              </Typography>
            </Stack>
            <Stack direction="row" sx={{ marginRight: "50px" }}>
              <Phone
                sx={{
                  fontSize: "16px",
                  marginRight: "4px",
                  color: "#F8F9F9",
                  marginTop: "3px",
                }}
              />
              <Typography sx={{ color: "#F8F9F9", marginTop: "1px" }}>
                {userData?.broker.phoneNumberUS}
              </Typography>
            </Stack>
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <div>
      <Button
        disableRipple
        aria-controls={open ? "dropdown-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        onMouseEnter={handleSummaryHoverEnter}
        onMouseLeave={handleSummaryHoverLeave}
        disabled={compLoading}
        sx={{
          color: isMobile ? theme.palette.primary.main : summaryHoverStyles.color,
          fontSize: "14px",
          "&.Mui-disabled": {
            color: isMobile ? theme.palette.secondary.main : summaryHoverStyles.color,
            opacity: 0.5,
          },
        }}>
        {isMobile ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
            }}>
            <EofyButtonIcon color={theme.palette.secondary.main} />
          </div>
        ) : (
          <DoubleCell
            left={isMobile || showText ? "" : "EOFY"}
            right={
              <EofyButtonIcon
                color={isMobile ? theme.palette.primary.main : summaryHoverStyles.color}
                background={!isMobile ? theme.palette.primary.main : summaryHoverStyles.color}
                width={17}
                height={17}
              />
            }
            tooltip="End of Financial Year Statement"
            justifyContent="right"
          />
        )}
      </Button>

      <Menu
        id="dropdown-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        style={{ marginTop: 6 }}>
        {yearOptions.map((year: number) => (
          <MenuItem key={year} onClick={() => handleYearSelect(year)}>
            {year}
          </MenuItem>
        ))}
      </Menu>
      {compLoading && <LinearProgress />}
      {isSuccess && reportIsSuccess && (
        // push off the header used for pdf generation off the screen so it will not be flashing when generating
        <div style={{ position: "absolute", right: "9999px" }}>
          {reportData && compLoading ? (
            <>
              <Header />
              <Footer />
            </>
          ) : (
            <div />
          )}
        </div>
      )}
    </div>
  );
};

export default EofyStatementButton;
